import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { displayAdminEventLabel } from "@/lib/datetime";

interface Event {
  id: string;
  title: string;
  start_time: string;
  timezone: string | null;
}

interface EventSelectForTestProps {
  value: string | null;
  onChange: (eventId: string | null) => void;
}

export function EventSelectForTest({ value, onChange }: EventSelectForTestProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      try {
        const now = new Date().toISOString();
        const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

        const { data, error } = await supabase
          .from("events")
          .select("id, title, start_time, timezone")
          .gte("start_time", now)
          .lte("start_time", thirtyDaysLater)
          .order("start_time", { ascending: true })
          .limit(20);

        if (error) throw error;
        setEvents(data || []);

        // Auto-select first event if none selected
        if (!value && data && data.length > 0) {
          onChange(data[0].id);
        }
      } catch (err) {
        console.error("Error fetching events:", err);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder="Loading events..." />
        </SelectTrigger>
      </Select>
    );
  }

  if (events.length === 0) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder="No upcoming events" />
        </SelectTrigger>
      </Select>
    );
  }

  return (
    <Select value={value || ""} onValueChange={(v) => onChange(v || null)}>
      <SelectTrigger>
        <SelectValue placeholder="Select an event for placeholders..." />
      </SelectTrigger>
      <SelectContent>
        {events.map((event) => (
          <SelectItem key={event.id} value={event.id}>
            <span className="max-w-[300px] truncate font-medium">
              {displayAdminEventLabel(event.title, event.start_time, event.timezone || undefined)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
